import './sso_standalone.css';
import '../../App.css';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allCountries } from 'country-telephone-data';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faUser,
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/fishtagged-logo-sso.png';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmailValid = (v) => EMAIL_RE.test(v.trim());
const isPhoneValid = (v) => /^\d{5,15}$/.test(v.trim());

const filteredCountries = allCountries.filter((c) => Number(c.dialCode) <= 999);
const priorityIso = ['us'];
const topCountries = filteredCountries.filter((c) => priorityIso.includes(c.iso2));
const restCountries = filteredCountries.filter((c) => !priorityIso.includes(c.iso2));
const countryList = [...topCountries, ...restCountries];

export default function SSOStandalonePage({ onLoginSuccess, user }) {
  const navigate = useNavigate();

  const [tab, setTab] = useState('login');
  const [loginMethod, setLoginMethod] = useState('email');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginDialCode, setLoginDialCode] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createDialCode, setCreateDialCode] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createPasswordConfirm, setCreatePasswordConfirm] = useState('');
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const getLoginFieldError = () => {
    if (loginMethod === 'email' && loginEmail && !isEmailValid(loginEmail))
      return 'Please enter a valid email address.';
    if (loginMethod === 'phone' && loginPhone && !isPhoneValid(loginPhone))
      return 'Please enter a valid phone number (digits only).';
    return '';
  };

  const getCreateErrors = () => {
    const errs = {};
    if (createEmail && !isEmailValid(createEmail))
      errs.email = 'Please enter a valid email address.';
    if (createPhone && !isPhoneValid(createPhone))
      errs.phone = 'Please enter a valid phone number (digits only).';
    if (createPassword && createPasswordConfirm && createPassword !== createPasswordConfirm)
      errs.confirm = 'Passwords do not match.';
    return errs;
  };

  const readUserCookie = () => {
    const rawCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('ft_user_data='))
      ?.split('=')[1];
    return rawCookie ? JSON.parse(atob(decodeURIComponent(rawCookie))) : {};
  };

  const handleLogin = async () => {
    setLoginError('');
    const fieldErr = getLoginFieldError();
    if (fieldErr) { setLoginError(fieldErr); return; }
    if (!loginPassword) { setLoginError('Please enter your password.'); return; }

    setLoginLoading(true);
    try {
      const payload = { authentication_route: 'PASSWORD', password: loginPassword };
      if (loginMethod === 'email') payload.email = loginEmail.trim();
      else { payload.tel_country_code = loginDialCode; payload.tel_number = loginPhone.trim(); }

      const form = new FormData();
      form.append('payload', JSON.stringify(payload));

      const res = await fetch(
        'https://access-control-api.fishtagged.org/session/initiate',
        { method: 'POST', body: form, credentials: 'include'}
      );
      const data = await res.json();

      if (data.status === 200) {
        const userData = readUserCookie();
        let displayName = userData.display_name || '';
        let accountType = userData.account_type || 'FULL';

        try {
          const checkRes = await fetch(
            'https://access-control-api.fishtagged.org/session/check',
            { credentials: 'include'}
          );
          const checkData = await checkRes.json();
          if (checkData.status === 200) {
            displayName = checkData.return_payload?.user?.display_name || displayName;
            accountType = checkData.return_payload?.user?.account_type || accountType;
          }
        } catch {}

        onLoginSuccess?.({
          name: displayName || loginEmail || loginPhone,
          accountType,
          isLoggedIn: true,
        });
        navigate('/');
      } else {
        const err = data.errors?.[0];
        const msg = typeof err === 'string' ? err : err?.explanation || 'Incorrect credentials. Please try again.';
        setLoginError(msg);
      }
    } catch {
      setLoginError('Something went wrong. Please check your connection.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreateError('');
    const errs = getCreateErrors();
    if (Object.keys(errs).length) {
      setCreateError(Object.values(errs)[0]);
      return;
    }
    if (!createName.trim()) { setCreateError('Display name is required.'); return; }
    if (!createEmail && !createPhone) { setCreateError('Please enter an email or phone number.'); return; }
    if (!createPassword) { setCreateError('Please enter a password.'); return; }
    if (createPassword !== createPasswordConfirm) { setCreateError('Passwords do not match.'); return; }

    setCreateLoading(true);
    try {
      const payload = {
        account_type: 'FULL',
        display_name: createName.trim(),
        proposed_password: createPassword,
        enabled_routes: ['PASSWORD'],
      };
      if (createEmail) payload.email = createEmail.trim();
      if (createPhone) { payload.tel_country_code = createDialCode; payload.tel_number = createPhone.trim(); }

      const form = new FormData();
      form.append('payload', JSON.stringify(payload));

      const res = await fetch(
        'https://access-control-api.fishtagged.org/account/create',
        { method: 'POST', body: form, credentials: 'include' }
      );
      const data = await res.json();

      if (data.status === 200) {
        const loginPayload = { authentication_route: 'PASSWORD', password: createPassword };
        if (createEmail) loginPayload.email = createEmail.trim();
        else { loginPayload.tel_country_code = createDialCode; loginPayload.tel_number = createPhone.trim(); }

        const lf = new FormData();
        lf.append('payload', JSON.stringify(loginPayload));
        const lr = await fetch(
          'https://access-control-api.fishtagged.org/session/initiate',
          { method: 'POST', body: lf, credentials: 'include'}
        );
        const ld = await lr.json();
        if (ld.status === 200) {
          const userData = readUserCookie();
          let displayName = userData.display_name || createName;
          let accountType = userData.account_type || 'FULL';

          try {
            const checkRes = await fetch(
              'https://access-control-api.fishtagged.org/session/check',
              { credentials: 'include'}
            );
            const checkData = await checkRes.json();
            if (checkData.status === 200) {
              displayName = checkData.return_payload?.user?.display_name || displayName;
              accountType = checkData.return_payload?.user?.account_type || accountType;
            }
          } catch {}

          onLoginSuccess?.({
            name: displayName,
            accountType,
            isLoggedIn: true,
          });
        }
        navigate('/');
      } else {
        const err = data.errors?.[0];
        const msg = typeof err === 'string' ? err : err?.explanation || 'Could not create account. Please try again.';
        setCreateError(msg);
      }
    } catch {
      setCreateError('Something went wrong. Please check your connection.');
    } finally {
      setCreateLoading(false);
    }
  };

  const loginValid =
    loginPassword.length > 0 &&
    (loginMethod === 'email' ? isEmailValid(loginEmail) : isPhoneValid(loginPhone));

  const createErrors = getCreateErrors();
  const createValid =
    createName.trim().length > 0 &&
    (isEmailValid(createEmail) || isPhoneValid(createPhone)) &&
    createPassword.length > 0 &&
    createPassword === createPasswordConfirm &&
    Object.keys(createErrors).length === 0;

  return (
    <>
      <div className="bg-image" />
      <div className="login-page">
        <div className="login-card">
			<div>
			  <img src={logo} alt="FishTagged Logo" className="sso-specific-logo"/>
			</div>
		  <div className="login-spacer" />
          <div className="login-tabs">
            <button
              className={`login-tab-btn${tab === 'login' ? ' active' : ''}`}
              onClick={() => { setTab('login'); setLoginError(''); }}
            >
              Sign In
            </button>
            <button
              className={`login-tab-btn${tab === 'create' ? ' active' : ''}`}
              onClick={() => { setTab('create'); setCreateError(''); }}
            >
              Create Account
            </button>
          </div>

          {tab === 'login' && (
            <>
              <div className="login-radio-group">
                <label className={`login-radio-label${loginMethod === 'email' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="loginMethod"
                    checked={loginMethod === 'email'}
                    onChange={() => { setLoginMethod('email'); setLoginError(''); }}
                  />
                  Login with email
                </label>
                <label className={`login-radio-label${loginMethod === 'phone' ? ' selected' : ''}`}>
                  <input
                    type="radio"
                    name="loginMethod"
                    checked={loginMethod === 'phone'}
                    onChange={() => { setLoginMethod('phone'); setLoginError(''); }}
                  />
                  Login with phone number
                </label>
              </div>

              {loginMethod === 'email' ? (
                <>
                  <div className="login-input-wrap">
                    <span className="login-input-icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    <input
                      type="email"
                      placeholder="Email address"
                      className={`login-input${loginEmail && !isEmailValid(loginEmail) ? ' error' : ''}`}
                      value={loginEmail}
                      onChange={(e) => { setLoginEmail(e.target.value); setLoginError(''); }}
                    />
                  </div>
                  {loginEmail && !isEmailValid(loginEmail) && (
                    <div className="login-error-msg">
                      <FontAwesomeIcon icon={faCircleExclamation} />
                      Invalid email address.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="login-phone-row">
                    <input
                      className="login-country-select"
                      value={loginDialCode}
                      onChange={(e) => setLoginDialCode(e.target.value)}
					  placeholder="Country code"
					  size={10}
                    / >
                    <input
                      type="tel"
                      placeholder="Phone number"
                      className={`login-input login-phone-input${loginPhone && !isPhoneValid(loginPhone) ? ' error' : ''}`}
                      value={loginPhone}
                      onChange={(e) => { setLoginPhone(e.target.value); setLoginError(''); }}
                    />
                  </div>
                  {loginPhone && !isPhoneValid(loginPhone) && (
                    <div className="login-error-msg">
                      <FontAwesomeIcon icon={faCircleExclamation} />
                      Invalid phone number (digits only, 5–15 digits).
                    </div>
                  )}
                </>
              )}

              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <FontAwesomeIcon icon={faLock} />
                </span>
                <input
                  type="password"
                  placeholder="Password"
                  className="login-input"
                  value={loginPassword}
                  onChange={(e) => { setLoginPassword(e.target.value); setLoginError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && loginValid && handleLogin()}
                />
              </div>

              {loginError && (
                <div className="login-error-msg">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  {loginError}
                </div>
              )}

              <button
                className="login-submit-btn"
                disabled={!loginValid || loginLoading}
                onClick={handleLogin}
              >
                {loginLoading ? 'Signing in…' : 'Sign in'}
                {!loginLoading && <FontAwesomeIcon icon={faChevronRight} />}
              </button>
            </>
          )}

          {tab === 'create' && (
            <>
              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <FontAwesomeIcon icon={faUser} />
                </span>
                <input
                  type="text"
                  placeholder="Display name"
                  className="login-input"
                  value={createName}
                  onChange={(e) => { setCreateName(e.target.value); setCreateError(''); }}
                />
              </div>

              <div className="login-phone-row">
                <input
                  className={`login-country-select${createPhone && !isPhoneValid(createPhone) ? ' error' : ''}`}
                  value={createDialCode}
                  onChange={(e) => setCreateDialCode(e.target.value)}
				  placeholder="Country code"
				  size={10}
				/ >
                <input
                  type="tel"
                  placeholder="Phone number"
                  className={`login-input login-phone-input${createPhone && !isPhoneValid(createPhone) ? ' error' : ''}`}
                  value={createPhone}
                  onChange={(e) => { setCreatePhone(e.target.value); setCreateError(''); }}
                />
              </div>
              {createPhone && !isPhoneValid(createPhone) && (
                <div className="login-error-msg">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  Invalid phone number (digits only, 5–15 digits).
                </div>
              )}

              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
                <input
                  type="email"
                  placeholder="Email address"
                  className={`login-input${createEmail && !isEmailValid(createEmail) ? ' error' : ''}`}
                  value={createEmail}
                  onChange={(e) => { setCreateEmail(e.target.value); setCreateError(''); }}
                />
              </div>
              {createEmail && !isEmailValid(createEmail) && (
                <div className="login-error-msg">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  Invalid email address.
                </div>
              )}

              <div className="login-spacer" />

              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <FontAwesomeIcon icon={faLock} />
                </span>
                <input
                  type="password"
                  placeholder="Password"
                  className="login-input"
                  value={createPassword}
                  onChange={(e) => { setCreatePassword(e.target.value); setCreateError(''); }}
                />
              </div>

              <div className="login-input-wrap">
                <span className="login-input-icon">
                  <FontAwesomeIcon icon={faLock} />
                </span>
                <input
                  type="password"
                  placeholder="Re-enter password"
                  className={`login-input${createPasswordConfirm && createPassword !== createPasswordConfirm ? ' error' : ''}`}
                  value={createPasswordConfirm}
                  onChange={(e) => { setCreatePasswordConfirm(e.target.value); setCreateError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && createValid && handleCreate()}
                />
              </div>

              {createPasswordConfirm && createPassword !== createPasswordConfirm && (
                <div className="login-error-msg">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  Passwords do not match.
                </div>
              )}

              {createError && (
                <div className="login-error-msg">
                  <FontAwesomeIcon icon={faCircleExclamation} />
                  {createError}
                </div>
              )}

              <button
                className="login-submit-btn"
                disabled={!createValid || createLoading}
                onClick={handleCreate}
              >
                {createLoading ? 'Creating account…' : 'Create Account'}
                {!createLoading && <FontAwesomeIcon icon={faChevronRight} />}
              </button>
			  
            </>
          )}

        </div>
		
      </div>

    </>
  );
}